/**
 * getEmpleadoNombre.ts — Helper para obtener el nombre del empleado a partir del usuarioId.
 * - Recibe un usuarioId y una lista de empleados (mock o real).
 * - Devuelve el nombre del empleado o "Desconocido" si no existe.
 * - Si se pasa un userName como fallback, se usa antes de "Desconocido".
 * - NO debe hacer fetch ni depender de backend.
 */

import type { Empleado } from "@/types";

/** Mínimo de un empleado que Caja necesita (resolución de nombres en tickets). */
export type EmpleadoNombre = Pick<Empleado, "id" | "nombre">;

export function getEmpleadoNombre(
  usuarioId: string,
  empleados: EmpleadoNombre[],
  fallbackName?: string,
): string {
  const emp = empleados.find((e) => e.id === usuarioId);
  if (emp) {
    // Si el nombre guardado es puramente numérico (un ID), formatear igual que el fallback
    if (/^\d+$/.test((emp.nombre || "").trim())) return `Emp. #${emp.nombre}`;
    return emp.nombre;
  }
  // Si fallbackName existe y no es puramente numérico, usarlo como nombre
  if (fallbackName && !/^\d+$/.test(fallbackName.trim())) return fallbackName;
  // Si el fallbackName es un número (ID), mostrarlo de forma más clara
  if (fallbackName) return `Emp. #${fallbackName}`;
  return "Desconocido";
}

/** Helper ligero para formatear nombres que pueden ser numéricos (IDs) */
export function fmtEmp(name?: string | null): string {
  if (!name) return "Empleado";
  if (/^\d+$/.test(name.trim())) return `Emp. #${name}`;
  return name;
}
